import {
  describeRepeat,
  firstOccurrence,
  nextOccurrence,
  type ReminderRepeatKind,
  type RepeatRule,
} from '@avslack/shared';

/**
 * Az ISMETLODES valasztoja - az emlekezteto es az utemezett uzenet kozos
 * resze (versenyaudit U9).
 *
 * Harom mezo, mert harom kulon dontes: mi a ritmus, mely napokon, es meddig.
 * A "meddig" URES is maradhat - a leggyakoribb eset az, hogy amig vissza nem
 * vonjak, es egy kotelezo veghatar ezt tenne korulmenyesse.
 *
 * Az idopontot SZANDEKOSAN nem duplazzuk meg kulon ora-mezovel: a "reggel 9"
 * onnan jon, ahova a kezdest allitottad. Egy masodik idopont-mezo mellett egy
 * percen belul kiderulne, hogy a ketto egymasnak mond ellent.
 */

const NAPOK = [
  { ertek: 1, cimke: 'H' },
  { ertek: 2, cimke: 'K' },
  { ertek: 3, cimke: 'Sze' },
  { ertek: 4, cimke: 'Cs' },
  { ertek: 5, cimke: 'P' },
  { ertek: 6, cimke: 'Szo' },
  { ertek: 0, cimke: 'V' },
];

/** A valasztott ismetlodes a szerver szabalyanak alakjaban. */
export function repeatRuleOf(params: {
  kind: ReminderRepeatKind;
  days: Set<number>;
  /** `YYYY-MM-DD`, vagy ures. */
  until: string;
  start: Date | null;
  timeZone: string;
}): RepeatRule {
  return {
    kind: params.kind,
    days: [...params.days],
    until: params.until ? new Date(`${params.until}T23:59`).toISOString() : null,
    anchor: params.start ? params.start.toISOString() : null,
    timeZone: params.timeZone,
  };
}

export function RepeatPicker({
  kind,
  onKind,
  days,
  onToggleDay,
  until,
  onUntil,
  start,
  rule,
}: {
  kind: ReminderRepeatKind;
  onKind: (kind: ReminderRepeatKind) => void;
  days: Set<number>;
  onToggleDay: (day: number) => void;
  until: string;
  onUntil: (until: string) => void;
  /** A kezdes - ebbol jon az ora es a havi nap. `null`: meg ervenytelen. */
  start: Date | null;
  rule: RepeatRule;
}) {
  /*
    Az elso alkalom heti sorozatnal elcsuszhat a kezdeshez kepest: kedden
    beallitott "hétfő, péntek" pentekkel indul. Ezt kimondjuk.
  */
  const elsoAlkalom = start && kind !== 'none' ? firstOccurrence(start, rule) : null;
  const elcsuszik = Boolean(start && elsoAlkalom && elsoAlkalom.getTime() !== start.getTime());
  const kovetkezoAlkalom =
    elsoAlkalom && !elcsuszik ? nextOccurrence(elsoAlkalom, rule) : elsoAlkalom;

  return (
    <div className="mt-3">
      <span className="text-2xs font-semibold uppercase tracking-wide text-ink-faint">
        Ismétlődés
      </span>
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <select
          aria-label="Ismétlődés"
          value={kind}
          onChange={(event) => onKind(event.target.value as ReminderRepeatKind)}
          className="rounded-control border border-border bg-surface-sunken px-2 py-1.5 text-sm"
        >
          <option value="none">Egyszeri</option>
          <option value="daily">Minden nap</option>
          <option value="weekly">Hetente</option>
          <option value="monthly">Havonta</option>
        </select>

        {/* A napok csak a hetinel latszanak. */}
        {kind === 'weekly' && (
          <div className="flex flex-wrap gap-1">
            {NAPOK.map((nap) => {
              const kivalasztva = days.has(nap.ertek);
              return (
                <button
                  key={nap.ertek}
                  type="button"
                  aria-pressed={kivalasztva}
                  onClick={() => onToggleDay(nap.ertek)}
                  className={`min-w-8 rounded-control border px-1.5 py-1 text-xs ${
                    kivalasztva
                      ? 'border-accent bg-accent/10 font-semibold text-accent'
                      : 'border-border-soft text-ink-muted hover:text-ink'
                  }`}
                >
                  {nap.cimke}
                </button>
              );
            })}
          </div>
        )}

        {kind !== 'none' && (
          <label className="flex items-center gap-1.5 text-xs text-ink-muted">
            eddig
            <input
              type="date"
              value={until}
              onChange={(event) => onUntil(event.target.value)}
              title="Üresen hagyva addig ismétlődik, amíg vissza nem vonod."
              className="rounded-control border border-border bg-surface-sunken px-2 py-1 text-xs"
            />
          </label>
        )}
      </div>

      {/*
        AZ ELONEZET. Egy "hetente" kapcsolo onmagaban nem mondja meg, melyik
        napon - ez a mondat igen. A kovetkezo alkalmat ugyanaz a fuggveny
        szamolja, ami a szerveren is lepteti a sorozatot.
      */}
      {kind !== 'none' && start && (
        <p className="mt-1.5 text-2xs text-ink-faint">
          {describeRepeat(start, rule)}
          {kovetkezoAlkalom
            ? ` · ${elcsuszik ? 'az első alkalom' : 'a kezdés utáni következő'}: ${kovetkezoAlkalom.toLocaleString(
                'hu-HU',
                {
                  month: 'short',
                  day: 'numeric',
                  weekday: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                },
              )}`
            : ' · a záró dátum előtt nincs több alkalom'}
        </p>
      )}
    </div>
  );
}
